import _asyncToGenerator from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/regenerator";
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Modal, Button, Tabs } from 'antd';
import styled from 'styled-components';
import { UploadFilesContainer } from './upload-files';
import { ImportUrlContainer } from './import-url';
import { SelectLibraryContainer } from './select-library';
import { useFileSelector } from '../hooks/use-file-selector.hook';
import { AttachmentFileType } from '../types/file-management.types';
import { convertFileV2ToFileAttachmentItem } from '../utils/file-type.utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var StyledModal = styled(Modal).withConfig({
  displayName: "file-selectorcontainer__StyledModal",
  componentId: "sc-1l9fncg-0"
})([".ant-modal-content{border-radius:16px;padding:24px;box-shadow:0 20px 25px -5px rgba(0,0,0,0.1),0 10px 10px -5px rgba(0,0,0,0.04);}.ant-modal-header{border-bottom:none;margin-bottom:0;padding:0;}.ant-modal-title{font-size:24px;font-weight:700;color:#1a2140;line-height:1.2;}.ant-modal-body{padding:0;}.ant-modal-footer{border-top:1px solid #f1f5f9;margin-top:32px;padding:24px 0 0 0;display:flex;justify-content:flex-end;gap:12px;}.ant-modal-close{top:24px;right:24px;}.ant-tabs-nav{&::before{border-bottom:1px solid #f1f5f9;}}.ant-tabs-tab{padding:8px 0;font-weight:500;color:#64748b;&-active .ant-tabs-tab-btn{color:#2563eb !important;}}.ant-tabs-ink-bar{background:#2563eb;height:2px;}"]);
var TitleWrapper = styled.div.withConfig({
  displayName: "file-selectorcontainer__TitleWrapper",
  componentId: "sc-1l9fncg-1"
})(["margin-bottom:24px;"]);
var Subtitle = styled.div.withConfig({
  displayName: "file-selectorcontainer__Subtitle",
  componentId: "sc-1l9fncg-2"
})(["font-size:15px;color:#64748b;margin-top:8px;font-weight:400;"]);
var FooterButton = styled(Button).withConfig({
  displayName: "file-selectorcontainer__FooterButton",
  componentId: "sc-1l9fncg-3"
})(["height:44px;border-radius:10px;font-weight:600;padding:0 24px;font-size:14px;transition:all 0.2s;margin-left:0 !important;"]);
var SaveButton = styled(FooterButton).withConfig({
  displayName: "file-selectorcontainer__SaveButton",
  componentId: "sc-1l9fncg-4"
})(["background-color:#2563eb;border-color:#2563eb;color:white;margin-left:0;&:hover{background-color:#1d4ed8 !important;border-color:#1d4ed8 !important;}&:disabled{background-color:#93c5fd;border-color:#93c5fd;color:white;}"]);
var tagConfigs = {
  upload: {
    title: 'Upload Local Files',
    subtitle: 'Select files from your device to upload into your chat.',
    label: 'Upload Local Files',
    actionLabel: 'Save'
  },
  url: {
    title: 'Import Files via URLs',
    subtitle: 'Add links to the files you want to import into your chat.',
    label: 'Import via URL',
    actionLabel: 'Import'
  },
  library: {
    title: 'File Library',
    subtitle: 'Browse and select files previously uploaded to your workspace.',
    label: 'File Library',
    actionLabel: 'Add to Chat'
  }
};
export var FileSelector = function FileSelector(_ref) {
  var open = _ref.open,
      onCancel = _ref.onCancel,
      onSave = _ref.onSave,
      isSaving = _ref.isSaving,
      _ref$maxFileUpload = _ref.maxFileUpload,
      maxFileUpload = _ref$maxFileUpload === void 0 ? 1 : _ref$maxFileUpload,
      allowedFileTypes = _ref.allowedFileTypes,
      _ref$defaultTab = _ref.defaultTab,
      defaultTab = _ref$defaultTab === void 0 ? 'upload' : _ref$defaultTab;

  var _useState = useState(defaultTab),
      activeTab = _useState[0],
      setActiveTab = _useState[1];

  var _useState2 = useState(false),
      uploading = _useState2[0],
      setUploading = _useState2[1];

  var _useState3 = useState(0),
      modalKey = _useState3[0],
      setModalKey = _useState3[1];

  var _useState4 = useState(0),
      forceUpdate = _useState4[0],
      setForceUpdate = _useState4[1];

  var _useState5 = useState([]),
      selectedLibraryFiles = _useState5[0],
      setSelectedLibraryFiles = _useState5[1];

  var _useState6 = useState(false),
      isUnmounting = _useState6[0],
      setIsUnmounting = _useState6[1];

  var uploadRef = useRef(null);
  var urlRef = useRef(null);
  var libraryRef = useRef(null); // Use centralized file selector state management

  var fileSelector = useFileSelector(); // Sync activeTab when modal opens with a different defaultTab

  useEffect(function () {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]); // Check if files are available for each tab

  var hasValidFiles = useMemo(function () {
    switch (activeTab) {
      case 'upload':
        // Check if upload ref has successfully uploaded files (processed and ready for upload)
        if (!uploadRef.current) {
          return false;
        }

        var uploadState = uploadRef.current.hasUploadedFiles;
        return uploadState;

      case 'url':
        // Check if import URL ref has valid URLs and is not validating
        if (!urlRef.current) {
          return false;
        }

        var urlState = urlRef.current.hasValidUrls && !urlRef.current.isValidating;
        return urlState;

      case 'library':
        // Check if library has selected files using state
        return selectedLibraryFiles.length > 0;

      default:
        return false;
    }
  }, [activeTab, forceUpdate, selectedLibraryFiles]); // Force update when tab changes to ensure fresh state

  useEffect(function () {
    setForceUpdate(function (prev) {
      return prev + 1;
    });
  }, [activeTab]); // Cleanup on unmount and when closing

  useEffect(function () {
    return function () {
      fileSelector.clearAll(); // Clear all refs to prevent memory leaks

      uploadRef.current = null;
      urlRef.current = null;
      libraryRef.current = null;
    };
  }, [fileSelector.clearAll]); // Additional cleanup when unmounting

  useEffect(function () {
    if (isUnmounting) {
      var _uploadRef$current, _urlRef$current, _libraryRef$current;

      // Force complete cleanup
      fileSelector.clearAll();
      (_uploadRef$current = uploadRef.current) === null || _uploadRef$current === void 0 ? void 0 : _uploadRef$current.clear();
      (_urlRef$current = urlRef.current) === null || _urlRef$current === void 0 ? void 0 : _urlRef$current.clear();
      (_libraryRef$current = libraryRef.current) === null || _libraryRef$current === void 0 ? void 0 : _libraryRef$current.clear();
      uploadRef.current = null;
      urlRef.current = null;
      libraryRef.current = null;
    }
  }, [isUnmounting, fileSelector.clearAll]);

  var handleSave = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var data, uploadedItems, urls, selectedFiles;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              data = [];
              _context.prev = 1;
              setUploading(true);

              if (!(activeTab === 'upload')) {
                _context.next = 11;
                break;
              }

              if (!uploadRef.current) {
                _context.next = 9;
                break;
              }

              _context.next = 7;
              return uploadRef.current.uploadFiles();

            case 7:
              uploadedItems = _context.sent;
              // Only return items that were successfully uploaded
              data = uploadedItems.filter(function (item) {
                return item.url && item.id;
              });

            case 9:
              _context.next = 12;
              break;

            case 11:
              if (activeTab === 'url') {
                // Get URLs from ImportUrlContainer
                if (urlRef.current) {
                  urls = urlRef.current.getUrls();
                  data = urls.map(function (url) {
                    return {
                      id: url,
                      name: url,
                      url: url,
                      fileType: AttachmentFileType.WEB_LINK,
                      type: 'LINK'
                    };
                  });
                }
              } else if (activeTab === 'library') {
                // Get selected files from library and convert to FileAttachmentItem format
                if (libraryRef.current) {
                  selectedFiles = libraryRef.current.getSelectedFiles();
                  data = selectedFiles.map(function (file) {
                    return convertFileV2ToFileAttachmentItem(file);
                  });
                } else {
                  // Fallback to state if ref is not available
                  data = selectedLibraryFiles.map(function (file) {
                    return convertFileV2ToFileAttachmentItem(file);
                  });
                }
              }

            case 12:
              if (data.length > 0) {
                onSave(data); // Clear library state after successful save to ensure fresh state on next open

                if (activeTab === 'library') {
                  setSelectedLibraryFiles([]);

                  if (libraryRef.current) {
                    libraryRef.current.clear();
                  }
                }
              }

              _context.next = 18;
              break;

            case 15:
              _context.prev = 15;
              _context.t0 = _context["catch"](1);
              console.error('Failed to save files:', _context.t0);

            case 18:
              _context.prev = 18;
              setUploading(false);
              return _context.finish(18);

            case 21:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[1, 15, 18, 21]]);
    }));

    return function handleSave() {
      return _ref2.apply(this, arguments);
    };
  }();

  var handleCancel = function handleCancel() {
    var _uploadRef$current2, _urlRef$current2, _libraryRef$current2;

    // Signal that we're unmounting
    setIsUnmounting(true); // Clear all tab states on cancel

    (_uploadRef$current2 = uploadRef.current) === null || _uploadRef$current2 === void 0 ? void 0 : _uploadRef$current2.clear();
    (_urlRef$current2 = urlRef.current) === null || _urlRef$current2 === void 0 ? void 0 : _urlRef$current2.clear();
    (_libraryRef$current2 = libraryRef.current) === null || _libraryRef$current2 === void 0 ? void 0 : _libraryRef$current2.clear(); // Reset file selector state completely

    fileSelector.clearAll(); // Reset local state

    setActiveTab('upload');
    setUploading(false);
    setSelectedLibraryFiles([]); // Increment modal key to force re-mount on next open

    setModalKey(function (prev) {
      return prev + 1;
    }); // Call parent onCancel after a short delay to ensure cleanup

    setTimeout(function () {
      setIsUnmounting(false);
      onCancel();
    }, 100);
  };

  var handleStateChange = useCallback(function () {
    setForceUpdate(function (prev) {
      return prev + 1;
    });
  }, []);
  var tabItems = useMemo(function () {
    return [{
      key: 'upload',
      label: tagConfigs.upload.label,
      children: /*#__PURE__*/_jsx(UploadFilesContainer, {
        ref: uploadRef,
        maxFileUpload: maxFileUpload,
        allowedFileTypes: allowedFileTypes,
        disabled: isSaving,
        hideUploadArea: fileSelector.isLimitReached(maxFileUpload),
        fileSelector: fileSelector,
        onStateChange: handleStateChange
      })
    }, {
      key: 'library',
      label: tagConfigs.library.label,
      children: /*#__PURE__*/_jsx(SelectLibraryContainer, {
        ref: libraryRef,
        disabled: isSaving,
        fileSelector: fileSelector,
        maxFileUpload: maxFileUpload,
        onSelectionChange: function onSelectionChange(files) {
          setSelectedLibraryFiles(files);
        },
        hideSelectionCounter: true
      })
    }, {
      key: 'url',
      label: tagConfigs.url.label,
      children: /*#__PURE__*/_jsx(ImportUrlContainer, {
        ref: urlRef,
        allowedFileTypes: allowedFileTypes,
        disabled: isSaving,
        hideUrlInput: fileSelector.isLimitReached(maxFileUpload),
        fileSelector: fileSelector,
        maxFileUpload: maxFileUpload,
        onStateChange: handleStateChange
      })
    }];
  }, [maxFileUpload, allowedFileTypes, isSaving, fileSelector, handleStateChange]);
  return /*#__PURE__*/_jsx(StyledModal, {
    open: open,
    onCancel: handleCancel,
    title: /*#__PURE__*/_jsxs(TitleWrapper, {
      children: [tagConfigs[activeTab].title, /*#__PURE__*/_jsx(Subtitle, {
        children: tagConfigs[activeTab].subtitle
      })]
    }),
    afterClose: function afterClose() {
      var _uploadRef$current3, _urlRef$current3, _libraryRef$current3;

      // Signal unmounting state
      setIsUnmounting(true); // Clear all refs before nullifying them

      (_uploadRef$current3 = uploadRef.current) === null || _uploadRef$current3 === void 0 ? void 0 : _uploadRef$current3.clear();
      (_urlRef$current3 = urlRef.current) === null || _urlRef$current3 === void 0 ? void 0 : _urlRef$current3.clear();
      (_libraryRef$current3 = libraryRef.current) === null || _libraryRef$current3 === void 0 ? void 0 : _libraryRef$current3.clear(); // Force complete state reset after modal is fully closed

      setActiveTab('upload');
      setUploading(false);
      setSelectedLibraryFiles([]); // Clear refs to prevent memory leaks

      uploadRef.current = null;
      urlRef.current = null;
      libraryRef.current = null; // Final cleanup of file selector state

      fileSelector.clearAll(); // Reset unmounting flag after cleanup

      setTimeout(function () {
        setIsUnmounting(false);
      }, 50);
    },
    footer: [/*#__PURE__*/_jsx(FooterButton, {
      onClick: handleCancel,
      disabled: isSaving,
      children: "Cancel"
    }, "cancel"), /*#__PURE__*/_jsx(SaveButton, {
      type: "primary",
      onClick: handleSave,
      loading: isSaving || uploading,
      disabled: isSaving || uploading || !hasValidFiles,
      children: tagConfigs[activeTab].actionLabel
    }, "save")],
    width: 600,
    centered: true,
    destroyOnClose: true,
    forceRender: false,
    children: /*#__PURE__*/_jsx(Tabs, {
      activeKey: activeTab,
      onChange: function onChange(key) {
        return setActiveTab(key);
      },
      items: tabItems,
      destroyInactiveTabPane: false
    })
  }, modalKey);
};